// ============================================
// STEP 4: TECHNICAL PREFERENCES
// ============================================

import { ArrowRight, ArrowLeft, Monitor, HardDrive, Star } from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter,
  Button, 
  SelectionCard,
} from '../../../components/ui';
import { useFormStore } from '../../../store';
import { 
  SCREEN_SIZE_OPTIONS,
  STORAGE_NEEDS_OPTIONS,
  BRAND_OPTIONS,
  PRIORITY_FEATURES,
} from '../../../constants';
import type { ScreenSizePreference, StorageNeeds } from '../../../types';
import styles from './Steps.module.css';

export function TechnicalPreferencesStep() {
  const { formData, updateTechnicalPreferences, nextStep, prevStep } = useFormStore();
  const { technicalPreferences } = formData;

  const handleBrandPreference = (brand: string) => {
    const current = technicalPreferences.brandPreference || [];
    if (current.includes(brand)) {
      updateTechnicalPreferences({ brandPreference: current.filter(b => b !== brand) });
    } else {
      // Also remove from avoid list if present
      const avoidList = technicalPreferences.brandAvoid || [];
      if (avoidList.includes(brand)) {
        updateTechnicalPreferences({ 
          brandAvoid: avoidList.filter(b => b !== brand),
          brandPreference: [...current, brand],
        });
      } else {
        updateTechnicalPreferences({ brandPreference: [...current, brand] });
      }
    }
  };

  const handleBrandAvoid = (brand: string) => {
    const current = technicalPreferences.brandAvoid || [];
    if (current.includes(brand)) {
      updateTechnicalPreferences({ brandAvoid: current.filter(b => b !== brand) });
    } else {
      // Also remove from preference list if present
      const prefList = technicalPreferences.brandPreference || [];
      if (prefList.includes(brand)) {
        updateTechnicalPreferences({ 
          brandPreference: prefList.filter(b => b !== brand),
          brandAvoid: [...current, brand],
        });
      } else {
        updateTechnicalPreferences({ brandAvoid: [...current, brand] });
      }
    }
  };

  const handlePriorityFeature = (feature: string) => {
    const current = technicalPreferences.priorityFeatures || [];
    if (current.includes(feature)) {
      updateTechnicalPreferences({ priorityFeatures: current.filter(f => f !== feature) });
    } else {
      // Limit to 5 features
      if (current.length < 5) {
        updateTechnicalPreferences({ priorityFeatures: [...current, feature] });
      }
    }
  };

  // Example: show a toast if user tries to proceed without selecting screen size or storage
  const handleNextStep = () => {
    // if (!technicalPreferences.screenSizePreference) {
    //   setToast('Please select a preferred screen size.');
    //   return;
    // }
    // if (!technicalPreferences.storageNeeds) {
    //   setToast('Please select your storage requirements.');
    //   return;
    // }
    nextStep();
  };

  return (
    <>
      {/* <Toast message={toast} onClose={() => setToast('')} /> */}
      <Card variant="elevated">
      <CardHeader>
        <CardTitle>Technical Preferences</CardTitle>
        <CardDescription>
          Let us know your hardware and brand preferences.
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {/* Screen Size */}
        <div className={styles.sectionTitle}>
          <h4>
            <Monitor size={18} className={styles.inlineIcon} />
            Preferred screen size
          </h4>
        </div>
        <div className={styles.optionsGrid}>
          {SCREEN_SIZE_OPTIONS.map((option) => (
            <SelectionCard
              key={option.value}
              selected={technicalPreferences.screenSizePreference === option.value}
              onClick={() => updateTechnicalPreferences({ screenSizePreference: option.value as ScreenSizePreference })}
              title={option.label}
              description={option.description}
              size="compact"
            />
          ))}
        </div>

        {/* Storage Needs */}
        <div className={styles.sectionTitle}>
          <h4>
            <HardDrive size={18} className={styles.inlineIcon} />
            Storage requirements
          </h4>
          <p className={styles.sectionSubtitle}>How much space do you need?</p>
        </div>
        <div className={styles.optionsGrid}>
          {STORAGE_NEEDS_OPTIONS.map((option) => (
            <SelectionCard
              key={option.value}
              selected={technicalPreferences.storageNeeds === option.value}
              onClick={() => updateTechnicalPreferences({ storageNeeds: option.value as StorageNeeds })}
              title={option.label}
              description={option.description}
              size="compact"
            />
          ))}
        </div>

        {/* Brand Preferences */}
        <div className={styles.sectionTitle}>
          <h4>Brand preferences</h4>
          <p className={styles.sectionSubtitle}>Any brands you prefer? (Optional)</p>
        </div>
        <div className={styles.optionsRow}>
          {BRAND_OPTIONS.filter(b => b !== 'No preference').map((brand) => (
            <button
              key={brand}
              type="button"
              className={`${styles.chipButton} ${technicalPreferences.brandPreference?.includes(brand) ? styles.chipActive : ''}`}
              onClick={() => handleBrandPreference(brand)}
            >
              {brand}
              {technicalPreferences.brandPreference?.includes(brand) && <span> ✓</span>}
            </button>
          ))}
        </div>

        {/* Brands to Avoid */}
        <div className={styles.sectionTitle}>
          <h4>Brands to avoid</h4>
          <p className={styles.sectionSubtitle}>Any brands you'd rather not consider? (Optional)</p>
        </div>
        <div className={styles.optionsRow}>
          {BRAND_OPTIONS.filter(b => b !== 'No preference' && b !== 'Apple').map((brand) => (
            <button
              key={brand}
              type="button"
              className={`${styles.chipButton} ${styles.chipDanger} ${technicalPreferences.brandAvoid?.includes(brand) ? styles.chipActiveRed : ''}`}
              onClick={() => handleBrandAvoid(brand)}
            >
              {brand}
              {technicalPreferences.brandAvoid?.includes(brand) && <span> ✗</span>}
            </button>
          ))}
        </div>

        {/* Priority Features */}
        <div className={styles.sectionTitle}>
          <h4>
            <Star size={18} className={styles.inlineIcon} />
            What matters most to you?
          </h4>
          <p className={styles.sectionSubtitle}>Select up to 5 priority features</p>
        </div>
        <div className={styles.optionsRow}>
          {PRIORITY_FEATURES.map((feature) => (
            <button
              key={feature}
              type="button"
              className={`${styles.chipButton} ${technicalPreferences.priorityFeatures?.includes(feature) ? styles.chipActive : ''}`}
              onClick={() => handlePriorityFeature(feature)}
              disabled={!technicalPreferences.priorityFeatures?.includes(feature) && (technicalPreferences.priorityFeatures?.length || 0) >= 5}
            >
              {feature}
            </button>
          ))}
        </div>
        {(technicalPreferences.priorityFeatures?.length || 0) > 0 && (
          <p className={styles.selectionCount}>
            {technicalPreferences.priorityFeatures?.length}/5 selected
          </p>
        )}
      </CardContent>

      <CardFooter className={styles.footerSpaceBetween}>
        <Button variant="outline" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </Button>
        <Button onClick={handleNextStep} size="lg">
          Continue
          <ArrowRight size={18} />
        </Button>
      </CardFooter>
      </Card>
    </>
  );
}
